import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';
import { sendRequest } from "./vmUtils";
import { lambdaURL } from "../config";
import { SlidingPanelSystem, usePanelNavigation } from "./vmSlidingPanel";
import { VMHome } from "./vmHome";
import { useAuth } from './vmAuthenticate';

import tada from '../sounds/tada.flac';

const SectionPanel = ({ 
  section, 
  questionsMap,
  answers, 
  onAnswerChange, 
  isLastSection,
  nextSection,
  sections,
  onFinish
}) => {
  const { pushPanel, resetToInitialPanel } = usePanelNavigation();
  const [localAnswers, setLocalAnswers] = useState(answers);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  const tada_sound = new Audio(tada);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get the questions for this section
  const sectionQuestions = questionsMap[section.SK.replace('METADATA#', '')];

  const handleNext = () => {
    if (isLastSection) {
      handleFinish();
    } else {
      setShowConfetti(true);
      //setTimeout(() => {
      //  console.log("Setting confetti to true! windowSize: " + JSON.stringify(windowSize));
      //  setShowConfetti(true);
      //}, 1000);
      setTimeout(() => {
        console.log("Setting confetti to false!");
        setShowConfetti(false);
      }, 2000);
      setTimeout(() => {
        pushPanel(
          <SectionPanel
            section={nextSection}
            questionsMap={questionsMap}
            answers={localAnswers}
            onAnswerChange={onAnswerChange}
            isLastSection={sections.indexOf(nextSection) === sections.length - 1}
            nextSection={sections[sections.indexOf(nextSection) + 1]}
            sections={sections}
            onFinish={onFinish}
          />
        );
      }, 2000);
    }
  };

  // Handle local state changes first, then propagate to parent
  const handleSelectChange = async (questionId, value) => {
    setLocalAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    await onAnswerChange(questionId, value);
  };

  const handleFinish = () => {
    setShowConfetti(true);
    tada_sound.play().catch(error => {
      console.log('Sound playback failed:', error);
    });

    // Redirect after celebration
    setTimeout(() => {
      setShowConfetti(false);
      window.location.href = '/';
    }, 4000);
  };

  return (
    <div className="vm-section" >
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          numberOfPieces={800}
          tweenDuration={3000}
          recycle={true}
          run={showConfetti}
          colors={['#2ecc71', '#3498db', '#e74c3c', '#f1c40f', '#9b59b6']}
          gravity={0.2}
        />
      )}
      <div className="vm-section-title">
        <h2>{section.title}</h2>
      </div>

      <div className="vm-form-vertical" >
        {sectionQuestions?.map(question => (
          <div key={question.SK} className="vm-form-group">
            <label className="vm-form-label mb-2">
              {question.questionText}
            </label>
            <select
              className="vm-form-input touch-action-manipulation"
              value={localAnswers[question.SK.replace('PROFILE_QUESTION#', '')] || ''}
              onChange={(e) => {
                const questionId = question.SK.replace('PROFILE_QUESTION#', '');
                handleSelectChange(questionId, e.target.value);
              }}
              onTouchStart={(e) => {
                // Prevent default touch behavior on mobile
                e.preventDefault();
                e.target.focus();
              }}
            >
              <option value="">Select an answer</option>
              {question.options.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        ))}

        <div className="vm-proposal-vote-button-container mt-6">
          <button 
            className="vm-form-button"
            onClick={handleNext}
          >
            {isLastSection ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const VMProfileQuestionnaire = ({ assemblyId }) => {
  const outerNavigation = usePanelNavigation();
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState({});
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const userId = user.userId;

  useEffect(() => {
    const fetchProfileData = async () => {
      setIsLoading(true);
      try {
        // First get all sections
        let jsonData = {
          command: "getProfileSections",
          assemblyId: assemblyId
        };
        const sectionsResponse = await sendRequest(lambdaURL, jsonData);
        
        if (sectionsResponse.body) {
          const parsedSections = JSON.parse(sectionsResponse.body);
          if (Array.isArray(parsedSections)) {
            setSections(parsedSections.sort((a, b) => a.order - b.order));
          } else {
            console.error("Sections data is not an array:", parsedSections);
            setSections([]);
          }

          // Then get questions for each section
          const questionMap = {};
          if (Array.isArray(parsedSections)) {
            for (const section of parsedSections) {
              try {
                const questionsResponse = await sendRequest(lambdaURL, {
                  command: "getProfileQuestions",
                  sectionId: section.SK.replace('METADATA#', '')
                });

                if (questionsResponse.body) {
                  const parsedQuestions = JSON.parse(questionsResponse.body);
                  if (Array.isArray(parsedQuestions)) {
                    questionMap[section.SK.replace('METADATA#', '')] =
                      parsedQuestions.sort((a, b) => a.order - b.order);
                  }
                }
              } catch (error) {
                console.error(`Error fetching questions for section ${section.SK}:`, error);
              }
            }
          }
          setQuestions(questionMap);
        }

        // Fetch user's existing answers if any
        const answersResponse = await sendRequest(lambdaURL, {
          command: "getUserProfileAnswers",
          assemblyId
        });

        if (answersResponse.body) {
          const parsedAnswers = JSON.parse(answersResponse.body);
          setAnswers(parsedAnswers);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching profile data:", error);
        setSections([]);
        setQuestions({});
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [userId, assemblyId]);

  const handleAnswerChange = async (questionId, value) => {
    try {
      await sendRequest(lambdaURL, {
        command: "saveProfileAnswer",
        assemblyId,
        questionId,
        answer: value
      });

      setAnswers(prev => ({
        ...prev,
        [questionId]: value
      }));
    } catch (error) {
      console.error("Error saving answer:", error);
    }
  };

  if (isLoading) {
    return <div className="vm-loading">Loading profile questions...</div>;
  }

  const InitialSectionPanel = () => (
    <SectionPanel
      section={sections[0]}
      questionsMap={questions}
      answers={answers}
      onAnswerChange={handleAnswerChange}
      isLastSection={sections.length === 1}
      nextSection={sections[1]}
      sections={sections}
      onFinish={outerNavigation.goBack}
    />
  );

  return (
    <SlidingPanelSystem initialPanel={<InitialSectionPanel />} />
  );
};

//export default VMProfileQuestionnaire;
